/* eslint-disable jsx-a11y/alt-text */
import Grid from "@mui/material/Grid";
import CardContent from "@mui/material/CardContent";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Color from "../../theme/color";
import { usePersistentState } from "../../contexts/global-context";
import { Tooltip, TooltipProps, styled, tooltipClasses } from "@mui/material";
import { STRING } from "../../resources/string";
import NumberFormat from "../../components/number-format";

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

export const DataTable = ({
  Data,
  retrievePaymentResponse,
  checkOutByID,
}: PriceTableProps) => {
  let parsedMetadata: any = {};
  if (Data) {
    if (typeof Data === "object") {
      // Data is already an object, no need to parse
      parsedMetadata = Data;
    } else {
      try {
        parsedMetadata = JSON.parse(Data);
      } catch (error) {
        console.error("Error parsing metadata data:", error);
      }
    }
  }
  const totalSum = parsedMetadata?.Products?.reduce((sum: any, item: any) => {
    return sum + Number(item?.UnitLabel);
  }, 0);
  const totalSumCheckOutById = checkOutByID?.orderItem?.reduce(
    (sum: any, item: any) => {
      return sum + Number(item?.qty);
    },
    0
  );

  const getTotalAmount = () => {
    if (parsedMetadata?.total) {
      return parsedMetadata?.total;
    }
    if (checkOutByID) {
      return checkOutByID?.orderDetail?.total;
    }
  };

  const getTotalQty = () => {
    if (parsedMetadata?.Amount) {
      return totalSum;
    }
    if (checkOutByID) {
      return totalSumCheckOutById;
    }
  };

  const groupedItems = checkOutByID?.orderItem?.reduce(
    (result: any, currentItem: any) => {
      const { ticketType, qty, total, ...rest } = currentItem;

      if (!result.some((item: any) => item.ticketType === ticketType)) {
        result.push({ ticketType, qty: 0, ...rest });
      }

      const foundItem = result.find(
        (item: any) => item.ticketType === ticketType
      );
      foundItem.qty += qty;
      return result;
    },
    []
  );
  const quantityByTicketConfigId: { [key: number]: number } = {};
  parsedMetadata?.ordersItems?.forEach((item: any) => {
    quantityByTicketConfigId[item.ticketConfigId] =
      (quantityByTicketConfigId[item.ticketConfigId] || 0) + 1;
  });
  const aggregatedItems: any = {};
  parsedMetadata?.ordersItems?.forEach((item: any) => {
    if (aggregatedItems[item.ticketType]) {
      aggregatedItems[item.ticketType].quantity += 1; // Increment quantity
      aggregatedItems[item.ticketType].price += item.price; // Accumulate price
    } else {
      // Initialize aggregated item
      aggregatedItems[item.ticketType] = {
        quantity: 1,
        price: item.price,
      };
    }
  });

  return (
    <Grid item xs={12} sm={12} md={12}>
      <CardContent style={{ padding: 0 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ fontSize: "18px", color: Color.color22, width: "60%" }}>Ticket Type</TableCell>
              <TableCell style={{ fontSize: "18px", color: Color.color22, textAlign: "center" }}>Quantity</TableCell>
              <TableCell style={{ fontSize: "18px", color: Color.color22, textAlign: "center" }}>Price</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {
              Object.keys(aggregatedItems).map((ticketType, index) => {
                const item = aggregatedItems[ticketType];
                return (
                  <TableRow key={index}>
                    <TableCell style={{ fontSize: "16px", color: Color.color21 }}>{ticketType}</TableCell>
                    <TableCell style={{ fontSize: "16px", color: Color.color21, textAlign: "center" }}><div>{item.quantity}</div></TableCell>
                    <TableCell style={{ fontSize: "16px", color: "#666", textAlign: "center" }}>$ <NumberFormat total={item.price} /></TableCell>
                  </TableRow>
                );
              })}
            {groupedItems?.map((item: any, index: number) => {
              const Total = item.qty * item.price;

              return (
                <TableRow key={index}>
                  <TableCell style={{ fontSize: "16px", color: Color.color21 }}>{item.ticketType}</TableCell>
                  <TableCell style={{ fontSize: "16px", color: Color.color21, textAlign: "center" }}><div>{item?.qty}</div></TableCell>
                  <TableCell style={{ fontSize: "16px", color: "#666", textAlign: "center" }}>$ <NumberFormat total={item?.price} /></TableCell>
                </TableRow>
              );
            })}
            <TableRow style={{ backgroundColor: Color.color11 }}>
              <TableCell style={{ fontSize: "18px", color: "#333", fontWeight: "bold" }}>Subtotal</TableCell>
              <TableCell style={{ fontSize: "18px", color: "#333", fontWeight: "bold", textAlign: "center" }}>{getTotalQty()}</TableCell>
              <TableCell style={{ fontSize: "18px", color: "#333", fontWeight: "bold", textAlign: "center" }}>
                $
                <NumberFormat total={parsedMetadata?.subTotal || checkOutByID?.orderDetail?.subTotal} />
              </TableCell>
            </TableRow>
          </TableBody>
          {
            checkOutByID?.orderDetail?.paymentType?.id === STRING.PAYMENT_TYPE_ID.ZELLE || parsedMetadata?.paymentTypeId === STRING.PAYMENT_TYPE_ID.CARD ||
              checkOutByID?.orderDetail?.paymentType?.id === STRING.PAYMENT_TYPE_ID.ISSUE_FREE_TICKET ? (
              <TableBody>
                <TableRow>
                  <TableCell style={{ fontSize: "16px", color: Color.color21 }}>Service Fee</TableCell>
                  <TableCell style={{ fontSize: "16px", color: Color.color21, textAlign: "center" }}>
                    <div></div>
                  </TableCell>
                  <TableCell style={{ fontSize: "16px", color: "#666", textAlign: "center" }}>
                    ${" "}
                    <NumberFormat total={parsedMetadata?.serviceFees || checkOutByID?.orderDetail?.serviceFees} />
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell style={{ fontSize: "16px", color: Color.color21 }}>Tax</TableCell>
                  <TableCell style={{ fontSize: "16px", color: Color.color21, textAlign: "center" }}>
                    <div></div>
                  </TableCell>
                  <TableCell style={{ fontSize: "16px", color: "#666", textAlign: "center" }}>
                    ${" "}
                    <NumberFormat total={parsedMetadata?.tax || checkOutByID?.orderDetail?.tax} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ fontSize: "16px", color: Color.color21 }}>Payment method</TableCell>
                  <TableCell style={{ fontSize: "16px", color: Color.color21, textAlign: "center" }}>
                    <div></div>
                  </TableCell>
                  <TableCell style={{ fontSize: "16px", color: "#666", justifyContent: "center", display: "flex" }}>
                    {
                      STRING.PAYMENT_TYPE.ZELLE === checkOutByID?.orderDetail?.paymentType?.displayName ?
                        (
                          <LightTooltip
                            title={
                              checkOutByID?.orderDetail?.paymentType?.displayName
                            }
                            placement="right"
                          >
                            <img
                              src={require("../../assets/icons/icons8-zelle-48.png")}
                              height={30}
                              style={{ marginRight: 10 }}
                            />
                          </LightTooltip>
                        )
                        :
                        (
                          <>
                            {
                              STRING.PAYMENT_TYPE.PAY_TO_HOST === checkOutByID?.orderDetail?.paymentType?.displayName ?
                                (
                                  <LightTooltip title={checkOutByID?.orderDetail?.paymentType?.displayName} placement="right">
                                    <img src={require("../../assets/icons/cash-on-delivery.png")} style={{ marginRight: 10 }} height={30} />
                                  </LightTooltip>
                                ) :
                                (
                                  <>
                                    {
                                      STRING.PAYMENT_TYPE.ISSUE_FREE_TICKET === checkOutByID?.orderDetail?.paymentType?.displayName ?
                                        (
                                          <LightTooltip title={parsedMetadata?.PaymentType} placement="right">
                                            <img src={require("../../assets/icons/free.png")} style={{ marginRight: 10 }} height={30} />
                                          </LightTooltip>
                                        )
                                        :
                                        (
                                          <LightTooltip title={parsedMetadata?.PaymentType} placement="right">
                                            <img src={require("../../assets/icons/credit.png")} style={{ marginRight: 10 }} height={30} />
                                          </LightTooltip>
                                        )
                                    }
                                  </>
                                )
                            }
                          </>
                        )
                    }
                  </TableCell>
                </TableRow>

                {parsedMetadata?.isCoupon ||
                  checkOutByID?.orderDetail?.discount > 0 ? (
                  <TableRow>
                    <TableCell style={{ fontSize: "16px", color: Color.color21 }}>
                      Coupon (
                      {retrievePaymentResponse?.metadata?.CouponCode ||
                        checkOutByID?.orderDetail?.couponCode}
                      )
                    </TableCell>
                    <TableCell style={{ fontSize: "16px", color: Color.color21, textAlign: "center", }}><div></div></TableCell>
                    <TableCell style={{ fontSize: "16px", display: "flex", fontWeight: "600", color: "orangered", justifyContent: "center", }}>
                      -${" "}{parsedMetadata?.discount || checkOutByID?.orderDetail?.discount}
                    </TableCell>
                  </TableRow>
                ) : null}
                <TableRow>
                  <TableCell
                    style={{
                      fontSize: "18px",
                      color: "#333",
                      fontWeight: "bold",
                    }}
                  >
                    Total
                  </TableCell>
                  <TableCell
                    style={{
                      fontSize: "18px",
                      color: "#333",
                      fontWeight: "bold",
                      textAlign: "center",
                    }}
                  ></TableCell>
                  <TableCell
                    style={{
                      fontSize: "18px",
                      color: "#333",
                      fontWeight: "bold",
                      textAlign: "center",
                    }}
                  >
                    $<NumberFormat total={getTotalAmount()} />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                <TableRow>
                  <TableCell style={{ fontSize: "16px", color: Color.color21 }}>
                    Payment method
                  </TableCell>
                  <TableCell style={{ fontSize: "16px", color: Color.color21, textAlign: "center" }}>
                    <div></div>
                  </TableCell>
                  <TableCell style={{ fontSize: "16px", color: "#666", display: "flex", justifyContent: "center", }}>
                    {
                      STRING.PAYMENT_TYPE.ZELLE === retrievePaymentResponse?.payment_method_types ? (
                        <LightTooltip title={"Zelle"} placement="right">
                          <img src={require("../../assets/icons/icons8-zelle-48.png")} height={30} style={{ marginRight: 10 }} />
                        </LightTooltip>
                      ) : (
                        <>
                          {
                            STRING.PAYMENT_TYPE.CARD === retrievePaymentResponse?.payment_method_types ? (
                              <LightTooltip title={"Card"} placement="right">
                                <img src={require("../../assets/icons/credit.png")} style={{ marginRight: 10 }} height={30} />
                              </LightTooltip>
                            ) :
                              (
                                <>
                                  {
                                    STRING.PAYMENT_TYPE.ISSUE_FREE_TICKET === retrievePaymentResponse?.payment_method_types ?
                                      <LightTooltip title={"Free"} placement="right">
                                        <img src={require("../../assets/icons/free.png")} style={{ marginRight: 10 }} height={30} />
                                      </LightTooltip> :

                                      <LightTooltip title={"Cash On Delivery"} placement="right">
                                        <img src={require("../../assets/icons/cash-on-delivery.png")} style={{ marginRight: 10 }} height={30} />
                                      </LightTooltip>
                                  }
                                </>
                              )
                          }
                        </>
                      )}
                  </TableCell>
                </TableRow>
                {
                  checkOutByID?.orderDetail?.discount > 0 ? (
                    <TableRow>
                      <TableCell style={{ fontSize: "16px", color: Color.color21 }}>Coupon({checkOutByID?.orderDetail?.couponCode})</TableCell>
                      <TableCell style={{ fontSize: "16px", color: Color.color21, textAlign: "center" }}><div></div></TableCell>
                      <TableCell style={{ fontSize: "16px", display: "flex", fontWeight: "600", color: "orangered", justifyContent: "center" }}>
                        - ${checkOutByID?.orderDetail?.discount}
                      </TableCell>
                    </TableRow>
                  ) : null
                }

                <TableRow>
                  <TableCell style={{ fontSize: "18px", color: "#333", fontWeight: "bold" }}>Total</TableCell>
                  <TableCell style={{ fontSize: "18px", color: "#333", fontWeight: "bold", textAlign: "center" }}></TableCell>
                  <TableCell style={{ fontSize: "18px", color: "#333", fontWeight: "bold", textAlign: "center" }}>
                    $ <NumberFormat total={getTotalAmount()} />
                  </TableCell>
                </TableRow>
              </TableBody>
            )
          }
        </Table>
      </CardContent>
    </Grid>
  );
};
interface PriceTableProps {
  Data?: any;
  checkOutByID?: any;
  retrievePaymentResponse?: any;
}
